/**
 * 🔔 Raid Inbox Pill — Header bell that lights up when this player's
 * castle was raided while they were away.
 *
 * - Polls /api/castle/raid-inbox/state every 20s.
 * - Renders a bell + red dot count when unseen > 0.
 * - Click opens the RaidHistoryModal which shows received + sent tabs.
 * - On open, automatically marks the inbox as seen (zeroes the badge).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bell } from "lucide-react";
import RaidHistoryModal from "./RaidHistoryModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RaidInboxPill({ className = "" }) {
  const [unseen, setUnseen] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const timer = useRef(null);

  const fetchState = async () => {
    try {
      const r = await axios.get(`${API}/castle/raid-inbox/state`);
      setUnseen(Number.parseInt(r.data?.unseen) || 0);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => {
    fetchState();
    timer.current = setInterval(fetchState, 20000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, []);

  // 🦅 Auto-open the modal when navigated here with ?open_inbox=1 (toast click-through)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("open_inbox") === "1") {
        setModalOpen(true);
        // Clean the URL so refresh doesn't re-open
        params.delete("open_inbox");
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
        window.history.replaceState({}, "", newUrl);
      }
    } catch (_e) { /* silent */ }
  }, []);

  const onOpen = async () => {
    setModalOpen(true);
    if (unseen > 0) {
      try {
        await axios.post(`${API}/castle/raid-inbox/mark-seen`);
        setUnseen(0);
      } catch (_e) { /* silent */ }
    }
  };

  // Hide the pill until the user has ever been raided. Keeps the HUD calm
  // for fresh accounts who haven't claimed a castle yet.
  if (unseen === 0) return (
    <>
      <button
        data-testid="header-raid-inbox-pill"
        onClick={onOpen}
        className={`relative flex items-center gap-1 border border-amber-900/60 px-2 py-1.5 bg-black/40 text-xs hover:border-amber-500 transition ${className}`}
        title="Raid History"
      >
        <Bell size={12} className="text-amber-400/70" />
      </button>
      {modalOpen && <RaidHistoryModal onClose={() => setModalOpen(false)} />}
    </>
  );

  return (
    <>
      <button
        data-testid="header-raid-inbox-pill"
        onClick={onOpen}
        className={`relative flex items-center gap-1 border border-red-700 px-2 py-1.5 bg-red-950/40 text-xs font-bold hover:bg-red-900/40 transition ${className}`}
        title={`You were raided ${unseen} time${unseen > 1 ? "s" : ""} — click to view`}
      >
        <Bell size={12} className="text-red-300 animate-pulse" />
        <span className="text-red-200 tabular-nums">{unseen}</span>
        <span
          data-testid="header-raid-inbox-dot"
          className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 animate-pulse shadow-[0_0_6px_rgba(239,68,68,0.8)]"
        />
      </button>
      {modalOpen && <RaidHistoryModal onClose={() => setModalOpen(false)} />}
    </>
  );
}
